/**
 * UCDP (Uppsala Conflict Data Program) event layer.
 * ScatterplotLayer with conflict-type-based coloring.
 * Radius scales with fatality count.
 * Matches WorldMonitor visual spec exactly.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';

/** Layer IDs handled by this renderer. */
export const UCDP_LAYER_IDS = new Set(['ucdp-events']);

/** Conflict type colors — matching WorldMonitor. */
const TYPE_COLORS: Record<string, [number, number, number, number]> = {
  'state-based': [255, 50, 50, 200],   // red
  'non-state':   [255, 165, 0, 200],   // orange
  'one-sided':   [255, 255, 0, 200],   // yellow
};

const DEFAULT_COLOR: [number, number, number, number] = [255, 50, 50, 200];

/** Get conflict type from event metadata. */
function getConflictType(event: NormalizedEvent): string {
  const raw = event.raw as Record<string, unknown> | undefined;
  const type = raw?.type_of_violence ?? raw?.conflictType;
  return typeof type === 'string' ? type : 'state-based';
}

/** Get fatality-based radius — matching WorldMonitor. */
function getUcdpRadius(event: NormalizedEvent): number {
  const raw = event.raw as Record<string, unknown> | undefined;
  const deaths = typeof raw?.deaths_best === 'number' ? raw.deaths_best : 1;
  return Math.max(4000, Math.sqrt(deaths) * 3000);
}

/** Create deck.gl layers for UCDP conflict events. */
export function createUcdpLayers(
  events: NormalizedEvent[],
  zoom: number,
): Layer[] {
  if (events.length === 0) return [];

  const opacity = Math.min(1, 0.3 + zoom * 0.1);

  return [
    new ScatterplotLayer<NormalizedEvent>({
      id: 'ucdp-events-dots',
      data: events,
      pickable: true,
      filled: true,
      radiusMinPixels: 3,
      radiusMaxPixels: 20,
      getPosition: (d) => d.location ? [d.location.lng, d.location.lat] : [0, 0],
      getFillColor: (d) => TYPE_COLORS[getConflictType(d)] ?? DEFAULT_COLOR,
      getRadius: (d) => getUcdpRadius(d),
      opacity,
      updateTriggers: {
        getFillColor: [events.length],
        getRadius: [events.length],
      },
    }),
  ];
}
